import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Avatar.module.css';

export type AvatarSize = 'sm' | 'md' | 'lg';
export type AvatarTone = 'neutral' | 1 | 2 | 3 | 4 | 5 | 6;

const toneClass: Record<AvatarTone, string | undefined> = {
  neutral: undefined,
  1: styles.tone1,
  2: styles.tone2,
  3: styles.tone3,
  4: styles.tone4,
  5: styles.tone5,
  6: styles.tone6,
};

/**
 * Picks a stable identity colour from a user id, so the same person is always
 * the same colour everywhere without anyone having to choose one.
 */
export function identityTone(seed: string): AvatarTone {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return ((Math.abs(hash) % 6) + 1) as AvatarTone;
}

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  size?: AvatarSize;
  tone?: AvatarTone;
  /** Image source. When omitted, `initials` is shown instead. */
  src?: string;
  /** Required alongside `src` for a meaningful accessible name. */
  alt?: string;
  initials?: string;
  /** Pulls the avatar left by the mockups' stacking offset, for building an
   *  overlapping group out of consecutive <Avatar>s. */
  overlap?: boolean;
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  { size = 'md', tone = 'neutral', src, alt = '', initials, overlap = false, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx(styles.avatar, styles[size], toneClass[tone], overlap && styles.overlap, className)}
      {...rest}
    >
      {src ? (
        <img className={styles.image} src={src} alt={alt} />
      ) : (
        <span aria-hidden={alt ? undefined : true}>{initials}</span>
      )}
    </div>
  );
});
